import React, { useEffect, useState } from 'react'
import { Component } from '@renderer/types/configState'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import Preview from '@renderer/components/Preview'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useConfigStore } from '@renderer/stores/configState'

/** Optional design-canvas layout for welcome config components that read context. */
export type ComponentMapperDesignLayout = { isCompact: boolean; isPortrait: boolean }

export const ComponentMapperDesignLayoutContext =
  React.createContext<ComponentMapperDesignLayout | null>(null)

interface ComponentMapperProps {
  components?: Component[]
  onSelectComponent?: (component: Component) => void
  selectedComponentId?: string
  onButtonAction?: (component: Component) => void
  designLayout?: ComponentMapperDesignLayout
  /**
   * `backdrop`: static letterbox duplicate — no camera preview, no motion timers (cheap duplicate of layout).
   */
  variant?: 'default' | 'backdrop'
}

const defaultDesignLayout: ComponentMapperDesignLayout = {
  isCompact: false,
  isPortrait: true
}

const ComponentMapper: React.FC<ComponentMapperProps> = ({
  components,
  onSelectComponent,
  onButtonAction,
  designLayout,
  variant = 'default'
}) => {
  const [animationTime, setAnimationTime] = useState(0)
  const { selectedCamera, cameraConnectionKey } = useSettingsStore()
  const { getFilter } = useConfigStore()
  const isBackdrop = variant === 'backdrop'

  useEffect(() => {
    if (isBackdrop) return
    const interval = setInterval(() => {
      setAnimationTime((prev) => prev + 16)
    }, 16)
    return (): void => clearInterval(interval)
  }, [isBackdrop])

  if (!components || components.length === 0) {
    return null
  }

  const layoutCtx = designLayout ?? defaultDesignLayout

  return (
    <ComponentMapperDesignLayoutContext.Provider value={layoutCtx}>
      {components.map((component, index) => {
        const { type, text, src, alt, style, id, buttonAction, previewKey, filterId, adjustments, fillContainer, cameraRatio } = component

        const duration = style?.animationDuration || 1000
        const progress = (animationTime % duration) / duration

        let transformStyle: string = ''

        if (!isBackdrop) {
          switch (style?.animation) {
            case 'pulse': {
              const pulseScale = 1 + Math.sin(progress * Math.PI * 2) * 0.2
              transformStyle = `scale(${pulseScale})`
              break
            }
            case 'bounce': {
              const bounceY = Math.sin(progress * Math.PI * 2) * 20
              transformStyle = `translateY(${bounceY}px)`
              break
            }
            case 'float': {
              const floatY = Math.sin(progress * Math.PI * 2) * 5
              transformStyle = `translateY(${floatY}px)`
              break
            }
            case 'shake': {
              const shakeX = Math.sin(progress * Math.PI * 8) * 5
              transformStyle = `translateX(${shakeX}px)`
              break
            }
            case 'wiggle': {
              const wiggleRotate = Math.sin(progress * Math.PI * 4) * 0.1
              transformStyle = `rotate(${wiggleRotate}rad)`
              break
            }
            case 'spin': {
              transformStyle = `rotate(${progress * 360}deg)`
              break
            }
            case 'wave': {
              const waveScale = 1 + Math.sin(progress * Math.PI * 2) * 0.1
              const waveRotate = Math.sin(progress * Math.PI * 2) * 0.05
              transformStyle = `scale(${waveScale}) rotate(${waveRotate}rad)`
              break
            }
            default:
              transformStyle = ''
          }
        }

        const wrapperStyle: React.CSSProperties = {
          position: 'absolute',
          top: component.top,
          left: component.left,
          width: component.width,
          height: component.height,
          zIndex: style?.zIndex,
          transform: transformStyle
        }

        const appliedStyle = {
          ...style,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          animationDuration: style?.animationDuration ? `${style.animationDuration}ms` : undefined
        }

        if (type === 'text') {
          return (
            <div
              key={id || `text-${index}`}
              onClick={() => onSelectComponent?.(component)}
              style={wrapperStyle}
            >
              <h1 style={appliedStyle}>{text}</h1>
            </div>
          )
        }

        if (type === 'image' && src) {
          return (
            <CachedImageComponent
              key={id || `image-${index}`}
              src={src}
              alt={alt || 'image'}
              style={appliedStyle}
              wrapperStyle={wrapperStyle}
              onSelect={() => onSelectComponent?.(component)}
            />
          )
        }

        if (type === 'button') {
          return (
            <div
              key={id || `button-${index}`}
              onClick={() => onSelectComponent?.(component)}
              style={wrapperStyle}
            >
              <button
                type="button"
                data-button-action={buttonAction}
                className="shadow-xl transition-transform transform active:scale-95"
                onClick={() => {
                  if (buttonAction) {
                    onButtonAction?.(component)
                  }
                }}
                style={appliedStyle}
              >
                {text}
              </button>
            </div>
          )
        }

        if (type === 'preview') {
          if (isBackdrop) {
            // Opaque editor-style placeholder (WelcomePreview.tsx) so session/letterbox colors do not bleed through when blurred.
            return (
              <div
                key={`preview-backdrop-${id || index}`}
                style={{
                  ...wrapperStyle,
                  backgroundColor: '#1F2937',
                  pointerEvents: 'none'
                }}
                aria-hidden
              />
            )
          }
          // Get filter adjustments if filterId is provided
          const filter = filterId ? getFilter(filterId) : null
          const filterAdjustments = adjustments || filter?.adjustments

          return (
            <div
              key={`preview-${id || index}-${selectedCamera || 'default'}-${cameraConnectionKey || 0}`}
              onClick={() => onSelectComponent?.(component)}
              style={wrapperStyle}
            >
              <Preview
                key={`preview-${id || index}-${selectedCamera || 'default'}-${cameraConnectionKey || 0}`}
                cameraKey={previewKey || 'primary'}
                filter={filterId}
                adjustments={filterAdjustments}
                fillContainer={fillContainer ?? false}
                cameraRatio={cameraRatio}
              />
            </div>
          )
        }

        return null
      })}
    </ComponentMapperDesignLayoutContext.Provider>
  )
}

// Separate component for cached images to use the hook
const CachedImageComponent: React.FC<{
  src: string
  alt: string
  style: React.CSSProperties
  wrapperStyle: React.CSSProperties
  onSelect: () => void
}> = ({ src, alt, style, wrapperStyle, onSelect }) => {
  const cachedSrc = useCachedImage(src)

  return (
    <div onClick={onSelect} style={wrapperStyle}>
      <img crossOrigin="anonymous" src={cachedSrc} alt={alt} style={style} />
    </div>
  )
}

export default ComponentMapper
